/*
 *  Copyright (c) 2003-2019 GameDuell GmbH, All Rights Reserved
 *  This document is strictly confidential and sole property of GameDuell GmbH, Berlin, Germany
 */
import MatchingWebsocketClient from './MatchingWebsocketClient'
import { MatchingState } from '@/model/Matching'
import { ERROR_CODES } from '@/model/ErrorCodes'
import { track, ACTION } from '@gd/tracking'
import { Commit } from 'vuex'

const state: MatchingState = {
  matchingData: {
    gameServerHost: '',
    gameVariant: '',
    gametype: '',
    lobbyId: '',
    numberOfPlayers: 1, // a default does not really make sense for this one but a default of 1 should prevent things breaking too easily
    slotId: '',
    ticket: '',
    tournamentId: '',
  },
}

export const mutations = {
  storeMatchingData(state: MatchingState, matchingData: GD_tournaments.MatchingData) {
    state.matchingData = matchingData
  },
}

let matchingClient: MatchingWebsocketClient

export const actions = {
  fetchMatching({ commit }: { commit: Commit }, tournamentId: string) {
    return fetch(`${process.env.VUE_APP_REST_URL}matching/${tournamentId}`, {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-cache',
    })
      .then((res: Response) => {
        if (res.status > 201) {
          commit('error', { id: 'fetchMatching', responseStatus: res.status }, { root: true })
          throw { errorCode: res.status === 401 ? ERROR_CODES.UNAUTHORIZED : ERROR_CODES.MATCHING_PROBLEM }
        }
        return res.json()
      })
      .then((data: GD_tournaments.MatchingData) => {
        commit('storeMatchingData', data)
      })
  },
  /**
   *
   * @param commit
   * @returns a Promise where the success case is "matching completed successfully" and the reject case covers all
   * "matching cancelled" cases (parameter given in callback will give more details why it was cancelled)
   */
  startMatching({ commit }: { commit: Commit }) {
    matchingClient = new MatchingWebsocketClient({
      slotId: state.matchingData.slotId,
      ticket: state.matchingData.ticket,
    })
    // Just for tracking purpose
    // We need to get the amount of online users when entering the lobby/matcing process
    matchingClient.updateUserCountCallback = (count: number) => {
      track({
        key: 'matching',
        action: ACTION.startMatching,
        gameType: state.matchingData.gametype,
        tournamentId: state.matchingData.tournamentId,
        lobbyUsers: count,
      })
      // Reset callback to avoid more invocations, i.e.
      // we just need the tracking call once the user is entering the automatching
      matchingClient.updateUserCountCallback = null
    }
    return matchingClient.start(state.matchingData.gameServerHost)
  },
  exitMatching({ commit }: { commit: Commit }) {
    if (matchingClient) {
      matchingClient.cancelMatching()
    }
  },
}

export const getters = {
  matchingData(state: MatchingState): GD_tournaments.MatchingData {
    return state.matchingData
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
